import AddAttachmentsRPTestReport from './add-attachments-rp-test-reports/AddAttachmentsRPTestReport'
import classes from './AddAttachmentsRP.module.scss'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { AddAttachmentsRPContext } from '../shared/contexts/AddAttachmentsRPContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCloudUpload } from '@fortawesome/free-solid-svg-icons'
import EcmIntegrationService from '../../../../../../services/EcmIntegrationService'
import { IEcmCategoryModel } from '../shared/interfaces/ecm-category-model/IECMCategoryModel'
import './AddAttachmentsRP.scss'
import useRPTestReportValidator from '../shared/hooks/useRPTestReportValidator'
import { useDispatch } from 'react-redux'
import { updateIsRPAttachmentValid } from '../../../../../../features/createCertificate/createCertificateSlice'
import { AddAttachmentsRPUtils } from '../shared/utils/AddAttachmentsRPUtils'
import {
  getAttributeUniqueName,
  CertificateGroupEnum,
} from '../../../../../../utils/constants/certificate.constants'
import { IAddAttachmentsRPTestReportModel } from '../shared/interfaces/IAddAttachmentsRPTestReportModel'
import { IAddAttachmentsRPTestSupportingDocumentModel } from '../shared/interfaces/IAddAttachmentsRPTestSupportingDocumentModel'
import { CertificateDocumentStatus } from '../../../../../../enums/CertificateDocumentStatus'
import saveAs from 'file-saver'
import useToastr from '../../../../../../hooks/useToastr'
import CertificateListService from '../../../../../../services/CertificateListService'
import { IAddAttachmentEditFormData } from '../shared/interfaces/non-model/IAddAttachmentEditFormData'
import { IConfirmationModalProps } from '../shared/interfaces/non-model/IConfirmationModalProps'
import ConfirmationModal from '../../../../../../shared/confirmation-modal/ConfirmationModal'
import { SchemeEcmType } from '../../../../../../enums/SchemeEcmType'

interface AddAttachmentsRPProps {
  isDisplayNone: boolean
  projectDetailsValues?: any
  certDetailsValues?: any
  certificateDetailsFromE2E?: any
  isCertificateFromRevision: boolean
  isEditOrModify: boolean
  rpAttachments: IAddAttachmentsRPTestReportModel[]
  setRPAttachments: React.Dispatch<React.SetStateAction<IAddAttachmentsRPTestReportModel[]>>
  certificateId?: string
  fromEditData?: IAddAttachmentEditFormData
  removeTitle?: boolean
  setHasUpdatedAttachments?: React.Dispatch<React.SetStateAction<boolean>>
  updateRPAttachmentOnParent?: (updatedRPAttachment: IAddAttachmentsRPTestReportModel[]) => void
  uniqueCertificateId?: string | undefined
  isAutoGenerate?: boolean
  certificateNumber?: string | undefined
  currentStep?: number
}

const AddAttachmentsRP = (props: AddAttachmentsRPProps) => {
  // #region States
  const [ecmCategoryList, setEcmCategoryList] = useState<IEcmCategoryModel[]>([])
  const [ecmDropdownLoading, setEcmDropdownLoading] = useState<boolean>(true)
  const [confirmationModalProps, setConfirmationModalProps] = useState<IConfirmationModalProps>(
    AddAttachmentsRPUtils.initialConfirmationModalValues
  )
  const { showError } = useToastr()
  // #endregion
  const dispatch = useDispatch()

  const certReadyRef = useRef(false)
  const revReadyRef = useRef(false)

  // #region Hooks
  const isTestReportValid = useRPTestReportValidator(props.rpAttachments)

  const mapStandardValueText = useCallback((): string => {
    let standardValuesText = ''

    if (
      props.certDetailsValues?.formValues &&
      props.certDetailsValues?.formValues[
        getAttributeUniqueName(CertificateGroupEnum.Certificate, 'standard')
      ]
    ) {
      const standardValuesTexts = props.certDetailsValues?.formValues[
        getAttributeUniqueName(CertificateGroupEnum.Certificate, 'standard')
      ]?.map((standard: any) => {
        const findStandardText = props.certDetailsValues?.standardOptions?.find(
          (standardOption: any) => standardOption.code === standard
        )

        if (findStandardText) return findStandardText?.name

        return ''
      })

      if (standardValuesTexts) standardValuesText = standardValuesTexts.join(', ')
    }

    return standardValuesText
  }, [props])

  //#region static
  const handleTestReportIssueDate = useCallback(() => {
    if (props?.rpAttachments?.length < 1) return

    const testReportIssueDates = AddAttachmentsRPUtils.getAttributeValueNullableCheck(
      props.certDetailsValues?.formValues,
      CertificateGroupEnum.Certificate,
      'testReportIssueDate'
    )

    if (testReportIssueDates.length == 1) {
      const cloneRP = structuredClone(props.rpAttachments).map(attachment => {
        if (!attachment?.ecmFileNodeId) attachment.issueDate = testReportIssueDates[0]

        attachment.supportingDocuments = attachment.supportingDocuments.map(supportingDoc => {
          if (!supportingDoc?.ecmFileNodeId) supportingDoc.issueDate = issueDate
          return supportingDoc
        })

        return attachment
      })

      props.setRPAttachments(cloneRP)
    } else if (testReportIssueDates.length > 1) {
      const testReportNumbers = AddAttachmentsRPUtils.getAttributeValueNullableCheck(
        props.certDetailsValues?.formValues,
        CertificateGroupEnum.Certificate,
        'testReportNumber'
      )

      const cloneRP = structuredClone(props.rpAttachments).map(attachment => {
        if (!attachment?.ecmFileNodeId) {
          const fileNameIndex = attachment.documentFileName.lastIndexOf('.')
          const fileName = attachment.documentFileName.slice(0, fileNameIndex)
          const reportNoIndex = testReportNumbers.findIndex(
            (report: any) => report.toLocaleLowerCase() === fileName.toLocaleLowerCase()
          )

          if (reportNoIndex >= 0) attachment.issueDate = testReportIssueDates[reportNoIndex]
          else attachment.issueDate = attachment.issueDate
        } else if (attachment?.ecmFileNodeId == '' && attachment?.issueDate == undefined)
          attachment.issueDate = null

        attachment.supportingDocuments = attachment.supportingDocuments.map(supp => {
          supp.issueDate = issueDate
          return supp
        })

        return attachment
      })

      props.setRPAttachments(cloneRP)
    }
  }, [
    props.certDetailsValues?.formValues?.[
      getAttributeUniqueName(CertificateGroupEnum.Certificate, 'testReportIssueDate')
    ],
  ])

  const projectNumber =
    props.projectDetailsValues?.selectedProjectDetails?.projectNumber ??
    props.projectDetailsValues?.projectNumber ??
    props.projectDetailsValues?.selectedProjectDetails?.serviceRequestNumber ??
    props.certificateDetailsFromE2E?.projectNumber

  const serviceRequestNumber =
    props.projectDetailsValues?.selectedProjectDetails?.serviceRequestNumber ??
    props.projectDetailsValues?.serviceRequestNumber ??
    props.certificateDetailsFromE2E?.serviceRequestNumber

  const originalIssueDate = AddAttachmentsRPUtils.getAttributeValueNullableCheck(
    props.certDetailsValues?.formValues,
    CertificateGroupEnum.Certify,
    'issueDate'
  )

  const revisionDate = AddAttachmentsRPUtils.getAttributeValueNullableCheck(
    props.certDetailsValues?.formValues,
    CertificateGroupEnum.Certify,
    'revisionDate'
  )

  const issueDate =
    props?.fromEditData?.revisionDate ||
    props.fromEditData?.issueDate ||
    revisionDate ||
    originalIssueDate

  const fileNumber =
    props.projectDetailsValues?.selectedProjectDetails?.project?.fileNo ??
    props.projectDetailsValues?.project?.fileNo ??
    props.projectDetailsValues?.selectedProjectDetails?.fileNo ??
    props.projectDetailsValues?.fileNo ??
    ''

  const referenceNumber = props.fromEditData
    ? props.fromEditData?.referenceNumber
    : !!AddAttachmentsRPUtils.getAttributeValueNullableCheck(
          props.certDetailsValues?.formValues,
          CertificateGroupEnum.Certificate,
          'testReportNumber'
        )
      ? AddAttachmentsRPUtils.getAttributeValueNullableCheck(
          props.certDetailsValues?.formValues,
          CertificateGroupEnum.Certificate,
          'testReportNumber'
        ).join(', ')
      : ''

  const standardNumber = props.fromEditData
    ? props.fromEditData?.standardNumber
    : mapStandardValueText()

  const certificateNumber = props.fromEditData
    ? props.fromEditData?.certificateNumber
    : props.isAutoGenerate
      ? props.certificateNumber
      : AddAttachmentsRPUtils.getAttributeValueNullableCheck(
          props.certDetailsValues?.formValues,
          CertificateGroupEnum.Certificate,
          'certificateName'
        )

  const certificateScheme = props.fromEditData
    ? props.fromEditData?.certificateScheme
    : AddAttachmentsRPUtils.getAttributeValueNullableCheck(
        props.certDetailsValues?.formValues,
        CertificateGroupEnum.Certificate,
        'certificateType'
      )

  const revisionNumber = props.fromEditData
    ? props.fromEditData?.revisionNumber
    : AddAttachmentsRPUtils.getAttributeValueNullableCheck(
        props.certDetailsValues?.formValues,
        CertificateGroupEnum.Certificate,
        'revisionNumber'
      )
  //#endregion

  const updateTestReportData = useCallback(() => {
    const updatedTestReportData = structuredClone(props.rpAttachments).map(testReportItem => {
      if (
        !AddAttachmentsRPUtils.isFieldReadonly(
          testReportItem.ecmFileNodeId,
          props.isCertificateFromRevision
        ) &&
        testReportItem.isValid
      ) {
        testReportItem.projectNumber = projectNumber
        testReportItem.issueDate = testReportItem.issueDate
        testReportItem.referenceNumber = referenceNumber
        testReportItem.standardNumber = standardNumber
        testReportItem.fileNumber ??= fileNumber
      }
      testReportItem.supportingDocuments = testReportItem.supportingDocuments.map(
        supportingDocs => {
          if (
            !AddAttachmentsRPUtils.isFieldReadonly(
              supportingDocs.ecmFileNodeId,
              props.isCertificateFromRevision
            ) &&
            supportingDocs.isValid
          ) {
            supportingDocs.projectNumber = projectNumber
            supportingDocs.issueDate =
              supportingDocs.contentGroup === 'TRCertificate' ? issueDate : null
            supportingDocs.standardNumber = standardNumber
            supportingDocs.fileNumber ??= fileNumber
          }
          return supportingDocs
        }
      )

      return testReportItem
    })
    props.setRPAttachments(updatedTestReportData)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectNumber, issueDate, fileNumber, referenceNumber, standardNumber])

  const updateSuppDocCertificateNumber = useCallback(() => {
    if (!certReadyRef.current) {
      certReadyRef.current = true
      return
    }

    if (!revReadyRef.current) {
      revReadyRef.current = true
      return
    }

    if (props.rpAttachments.length > 0) {
      const updatedTestReportData = structuredClone(props.rpAttachments).map(testReportItem => {
        testReportItem.supportingDocuments = testReportItem.supportingDocuments.map(
          supportingDocs => {
            if (
              AddAttachmentsRPUtils.isFieldReadonly(
                supportingDocs.ecmFileNodeId,
                props.isCertificateFromRevision
              ) &&
              supportingDocs.isValid
            )
              return supportingDocs

            supportingDocs.certificateNumber = AddAttachmentsRPUtils.formatCertificateNumber(
              certificateNumber,
              revisionNumber
            )

            supportingDocs.issueDate =
              supportingDocs.contentGroup === 'TRCertificate' ? issueDate : null

            return supportingDocs
          }
        )

        return testReportItem
      })

      props.setRPAttachments(updatedTestReportData)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificateNumber, revisionNumber])

  useEffect(() => {
    if (updateTestReportData) updateTestReportData()

    return () => {}
  }, [updateTestReportData])

  useEffect(() => {
    handleTestReportIssueDate()

    return () => {}
  }, [handleTestReportIssueDate])

  useEffect(() => {
    if (updateSuppDocCertificateNumber) updateSuppDocCertificateNumber()

    return () => {}
  }, [updateSuppDocCertificateNumber])

  useEffect(() => {
    dispatch(updateIsRPAttachmentValid(isTestReportValid))

    return () => {}
  }, [isTestReportValid, dispatch])

  const getRPCategoryAttributeList = useCallback(async () => {
    setEcmDropdownLoading(true)
    const result = await EcmIntegrationService.getRPCategoryAttributeList()

    setEcmCategoryList(result.data)
    setEcmDropdownLoading(false)
  }, [])

  useEffect(() => {
    getRPCategoryAttributeList()
  }, [getRPCategoryAttributeList])

  const shouldUpdateRPAttachments = () => {
    if (props.setHasUpdatedAttachments) props.setHasUpdatedAttachments(true)
  }

  //#endregion

  const downloadFile = async (
    file: IAddAttachmentsRPTestReportModel | IAddAttachmentsRPTestSupportingDocumentModel
  ) => {
    switch (file.status) {
      case CertificateDocumentStatus.LinkedToWbAndEcmTic:
      case CertificateDocumentStatus.SavedInEcmTic:
        await EcmIntegrationService.downloadEcmFile(file.ecmFileNodeId ?? '').then(response => {
          if (response) {
            const fileName = file.ecmFileNodeId
              ? (file.ecmFileName ?? '')
              : (file.documentFileName ?? file.ecmFileName)

            saveAs(response, fileName)
          } else {
            showError(
              'Unable to Download File',
              'There is a problem while connecting to ECM Folder. Please try again later.'
            )
          }
        })
        break
      case CertificateDocumentStatus.UploadToEcmFailed:
      case CertificateDocumentStatus.ReadyToLinkToWbAndEcmTic:
      case CertificateDocumentStatus.TestReportDuplicateLinkToEcmFailed:
        await CertificateListService.downloadCertificateDocument(file.id).then(response => {
          if (response) {
            saveAs(response, file.documentFileName)
          } else {
            showError('Error!', 'File does not exists.')
          }
        })
        break
    }
  }

  //#region Inline Components
  const InlineDropzoneActiveOverlay = () => {
    return (
      <div className={classes['add-attachments-rp-dropzone-drag-overlay']}>
        <div className={classes['add-attachments-rp-dropzone-drag-overlay-content']}>
          <div className={classes['add-attachments-rp-dropzone-drag-overlay-content-icon']}>
            <FontAwesomeIcon icon={faCloudUpload} size="2x" />
          </div>
          <div className={classes['add-attachments-rp-dropzone-drag-overlay-content-text']}>
            Drop files for upload
          </div>
        </div>
      </div>
    )
  }

  //#endregion

  return (
    <AddAttachmentsRPContext.Provider
      value={{
        ecmCategoryModel: ecmCategoryList,
        ecmCategoryDropdownLoading: ecmDropdownLoading,
      }}
    >
      <div
        style={{
          display: props.isDisplayNone ? 'none' : '',
        }}
        className={classes['add-attachments-rp-container']}
      >
        <div className={classes['add-attachments-rp-container-header']}>
          {!props.removeTitle && (
            <span className={classes['add-attachments-rp-container-header-title']}>
              Attachments
            </span>
          )}

          <span className={classes['add-attachments-rp-container-header-note']}>
            Note: Maximum upload file size: (50MB) | Allowed File Type: (PDF)
          </span>
        </div>
        <div className={classes['add-attachments-rp-container-body']}>
          <AddAttachmentsRPTestReport
            rpAttachments={props.rpAttachments}
            setRPAttachments={props.setRPAttachments}
            projectNumber={projectNumber}
            serviceRequestNumber={serviceRequestNumber}
            issueDate={issueDate}
            fileNumber={fileNumber}
            referenceNumber={referenceNumber}
            standardNumber={standardNumber}
            certificateNumber={certificateNumber}
            certificateScheme={certificateScheme}
            revisionNumber={revisionNumber}
            dropzoneUploadClassName={classes['add-attachments-rp-dropzone-wrapper']}
            dropzoneUploadDragElement={<InlineDropzoneActiveOverlay />}
            isCertificateFromRevision={props.isCertificateFromRevision}
            downloadFile={downloadFile}
            setConfirmationModalProps={setConfirmationModalProps}
            shouldUpdateRPAttachments={shouldUpdateRPAttachments}
            updateRPAttachmentOnParent={props.updateRPAttachmentOnParent}
            certificateId={props.certificateId}
            isEditOrModify={props.isEditOrModify}
            formValues={props?.certDetailsValues?.formValues}
            uniqueCertificateId={props.uniqueCertificateId}
            currentStep={props.currentStep}
          />
        </div>
      </div>
      <ConfirmationModal {...confirmationModalProps} />
    </AddAttachmentsRPContext.Provider>
  )
}

export default AddAttachmentsRP
